// A dataset's routing rules, in Task Replay's recommendation style: which slice of its work (task type, complexity,
// repo) can move from the model it is on to a cheaper one, what that saves, and how far behind the cheaper one could
// be. The evidence is per slice and per two models (taskreplay_routing); the rules are computed here from it, so
// changing the margin, the baseline or the savings asked for repaints without a fetch.
import { state, esc } from '../core'
import {
  DEFAULT_MARGIN, DEFAULT_MIN_SAVINGS, asGoodOrBetter, defaultBaseline, expectedScore, recommend, sliceLabel,
  type Recommendation, type RoutingRow,
} from '../../shared/replay'
import { goReplay } from './index'
import { modelName, money, pct, signed } from './fmt'

/** The reader's settings (the URL's, else the defaults) and what the evidence says at them. */
function judged(rows: RoutingRow[]) {
  const choices = [...new Set(rows.map((r) => r.baseline))]
  const base = choices.indexOf(state.rp.base) >= 0 ? state.rp.base : defaultBaseline(rows)
  const margin = Number(state.rp.margin) > 0 ? Number(state.rp.margin) : DEFAULT_MARGIN
  const save = Number(state.rp.save) > 0 ? Number(state.rp.save) : DEFAULT_MIN_SAVINGS
  return { choices, base, margin, save, ...recommend(rows, { baseline: base, margin, minSavings: save }) }
}

/** The dataset's conclusion at the reader's settings, for its header: its broadest rule, or why there is none. */
export function conclusion(rows: RoutingRow[]): string {
  if (!rows.length) return ''
  const { base, margin, routes, inconclusive } = judged(rows)
  const box = (tone: string, html: string) => '<div class="rp-verdict rp-verdict-' + tone + '">' + html + '</div>'
  const r = routes[0]
  if (r) {
    return box('good', '<b>' + esc(sliceLabel(r.row.slice)) + '</b> → <b>' + esc(modelName(r.row.candidate)) + '</b>' +
      '<span class="rp-dot">·</span>' + pct(r.row.cost.savings) + ' cheaper than ' + esc(modelName(base)) + '<span class="rp-dot">·</span>' +
      (r.bound < 0 ? 'at most ' + Math.round(-r.bound) + ' Elo behind' : 'no worse') + asGood(r) + ' (' + pct(r.level, r.level > 0.98 ? 1 : 0) + ')' +
      (routes.length > 1 ? '<span class="rp-dot">·</span>' + (routes.length - 1) + ' more rule' + (routes.length > 2 ? 's' : '') : ''))
  }
  const c = inconclusive[0]
  if (c) {
    return box('mid', 'No rule yet<span class="rp-dot">·</span>closest: <b>' + esc(sliceLabel(c.row.slice)) + '</b> → <b>' + esc(modelName(c.row.candidate)) + '</b>' +
      (c.moreTasks ? ', about ' + c.moreTasks + ' more tasks to settle' : ''))
  }
  return box('none', 'No cheaper model is within ' + margin + ' Elo of ' + esc(modelName(base)) + '.')
}

export function routingBlock(rows: RoutingRow[]): string {
  const head = (sub: string) => '<div class="tr-block-head"><h3>Routing</h3><span class="tr-sub">' + sub + '</span></div>'
  if (!rows.length) return '<div class="tr-block" id="rpRouting">' + head('') + '<div class="empty">No routing evidence yet.</div></div>'
  const { choices, base, margin, save, routes, inconclusive } = judged(rows)
  const controls = '<div class="rp-settings">For traffic on ' +
    '<select class="rp-inline" id="rpBase">' + choices.map((m) =>
      '<option value="' + esc(m) + '"' + (m === base ? ' selected' : '') + '>' + esc(modelName(m)) + '</option>').join('') + '</select>, a margin of ' +
    '<input class="rp-inline rp-margin" id="rpMargin" type="number" min="5" max="200" step="5" value="' + margin + '" ' +
      'data-tip="' + esc(marginTip(rows, base, margin)) + '"> Elo, and at least ' +
    '<select class="rp-inline" id="rpSave">' + [0.1, 0.2, 0.3, 0.5].map((s) =>
      '<option value="' + s + '"' + (s === save ? ' selected' : '') + '>' + pct(s) + '</option>').join('') + '</select> saved</div>'
  const cards = routes.length
    ? '<div class="tr-recos">' + routes.map((r) => ruleCard(r, margin)).join('') + '</div>'
    : '<div class="empty">' + (inconclusive.length ? 'No rule at these settings yet: the close calls are below.' : 'No cheaper model is within the margin.') + '</div>'
  return '<div class="tr-block" id="rpRouting">' + head(routes.length + ' rule' + (routes.length === 1 ? '' : 's')) + controls + cards + '</div>' +
    closeCalls(inconclusive)
}

export function wireRouting(root: HTMLElement): void {
  const el = (id: string) => root.querySelector('#' + id) as HTMLInputElement | HTMLSelectElement | null
  const base = el('rpBase'), save = el('rpSave'), margin = el('rpMargin')
  if (base) base.onchange = () => goReplay({ base: base.value }, { replace: true })
  if (save) save.onchange = () => goReplay({ save: save.value }, { replace: true })
  if (margin) margin.onchange = () => {
    const v = Math.round(Number(margin.value))
    goReplay({ margin: v > 0 ? String(v) : '' }, { replace: true })
  }
}

function ruleCard(r: Recommendation, margin: number): string {
  const row = r.row
  const j = row.parts.judge
  const bound = (r.bound < 0 ? '≤ ' + Math.round(-r.bound) + ' Elo behind' : 'ahead even at the bound') + asGood(r)
  return '<div class="tr-reco q"><div class="tr-reco-kind">' + esc(sliceLabel(row.slice)) + '</div>' +
    '<div class="tr-reco-model">' + esc(modelName(row.candidate)) + '</div>' +
    '<div class="tr-reco-agent">from ' + esc(modelName(row.baseline)) + '</div>' +
    '<div class="tr-reco-big"><span class="tr-reco-v">' + pct(row.cost.savings) + '</span><span class="tr-reco-u">cheaper per task</span></div>' +
    '<div class="tr-reco-why">' + bound + ' (' + pct(r.level, r.level > 0.98 ? 1 : 0) + ') · ' + money(row.cost.baseline) + ' → ' +
      money(row.cost.candidate) + ' · judge ' + j.wins + '–' + j.ties + '–' + j.losses + ' · ' + row.tasks + ' tasks</div>' +
    '<div class="tr-reco-spark">' + strip(row.gap, r.bound, margin) + '</div></div>'
}

/** At the rule's bound, the share of tasks the cheaper model's work is judged as good or better on. */
function asGood(r: Recommendation): string {
  const share = asGoodOrBetter(r.bound, r.row.parts.judge)
  return share == null ? '' : ' · as good or better on ≥ ' + pct(share) + ' of tasks'
}

/** What the margin means against the baseline, at the tie rate of its pairs over the whole dataset. */
function marginTip(rows: RoutingRow[], base: string, margin: number): string {
  const whole = rows.filter((r) => r.baseline === base && !Object.values(r.slice).some(Boolean))
  const record = whole.reduce((a, r) => ({ wins: a.wins + r.parts.judge.wins, ties: a.ties + r.parts.judge.ties,
    losses: a.losses + r.parts.judge.losses }), { wins: 0, ties: 0, losses: 0 })
  const share = asGoodOrBetter(-margin, record)
  return share == null ? 'expected score ≥ ' + expectedScore(-margin).toFixed(2) + ' against ' + modelName(base)
    : 'as good or better than ' + modelName(base) + ' on ≥ ' + pct(share) + ' of tasks'
}

/** The gap (dot) and its bound (bar) on a strip from 150 behind to 100 ahead, with even and the margin marked. */
function strip(gap: number, bound: number, margin: number): string {
  const lo = -150, hi = 100, w = 300, x = (v: number) => ((Math.max(lo, Math.min(hi, v)) - lo) / (hi - lo)) * w
  const tip = 'gap ' + signed(gap) + ' Elo\nbound ' + signed(bound) + ' · margin −' + margin
  return '<svg class="rp-strip" viewBox="0 0 ' + w + ' 22" role="img" aria-label="' + esc(tip.replace('\n', ', ')) + '" data-tip="' + esc(tip) + '">' +
    '<line x1="0" y1="11" x2="' + w + '" y2="11" class="rp-strip-axis"/>' +
    '<line x1="' + x(0) + '" y1="4" x2="' + x(0) + '" y2="18" class="rp-strip-zero"/>' +
    '<line x1="' + x(-margin) + '" y1="2" x2="' + x(-margin) + '" y2="20" class="rp-strip-margin"/>' +
    '<line x1="' + x(bound) + '" y1="11" x2="' + x(gap) + '" y2="11" class="rp-strip-iv"/>' +
    '<circle cx="' + x(gap) + '" cy="11" r="4" class="rp-strip-pt"/></svg>'
}

function closeCalls(list: Recommendation[]): string {
  if (!list.length) return ''
  return '<div class="tr-block"><div class="tr-block-head"><h3>Close calls</h3><span class="tr-sub">' + list.length + '</span></div>' +
    '<div class="tr-card tr-card-flat"><table class="tr-lb2"><thead><tr><th>Slice</th><th>To</th><th class="r">Saves</th><th class="r">Gap</th>' +
    '<th class="r">Bound</th><th class="r">Tasks</th><th class="r">To settle</th></tr></thead><tbody>' +
    list.map((r) => '<tr><td>' + esc(sliceLabel(r.row.slice)) + '</td><td>' + esc(modelName(r.row.candidate)) + '</td>' +
      '<td class="r">' + pct(r.row.cost.savings) + '</td><td class="r">' + signed(r.row.gap) + '</td>' +
      '<td class="r">' + signed(r.bound) + '</td><td class="r">' + r.row.tasks + '</td>' +
      '<td class="r">' + (r.moreTasks != null ? '+' + r.moreTasks + ' tasks' : '—') + '</td></tr>').join('') +
    '</tbody></table></div></div>'
}
